import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import type { QuestionType } from '@examing/types';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { QuestionAuthoringFields, type QuestionAuthoringValue } from 'components/questionBank/QuestionAuthoringFields';
import { QuestionValidationProvider, useQuestionValidationFeedback } from 'components/questions/QuestionField';
import { validateQuestion } from 'components/questions/questionValidation';
import type { QuestionTypeData } from 'components/questions/QuestionTypeEditor';
import { QUESTION_TYPE_META } from 'pages/QuestionBank/questionTypeMeta';
import { normalizeQuestionAnswers } from 'lib/questionOptions';
import { SAT_DOMAINS, TYPES_FOR_KIND, isSatKind } from '../../constants';
import type { IeltsWritingTask, QuestionDifficulty, SectionKind } from '../../types';
import { SprAnswersEditor } from './SprAnswersEditor';
import { SpeakingEditor, speakingDraftFrom, speakingDraftIssue, speakingPartsFrom } from './SpeakingEditor';

const TYPE_FIELDS: (keyof QuestionTypeData)[] = [
  'answerMode', 'acceptedAnswers', 'matchingOptions', 'maxWords', 'blanks', 'reorderItems',
  'correctOrder', 'rubric', 'mediaUrl', 'passageText', 'passageTitle', 'subQuestions', 'pages',
];

const fresh = (type: QuestionType): QuestionAuthoringValue => ({
  type,
  prompt: '',
  explanation: '',
  matchPairs: [{ key: '', value: '' }, { key: '', value: '' }],
  options:
    type === 'SPR' || type === 'OPEN_ENDED' || type === 'PASSAGE' || type === 'SPEAKING'
      ? []
      : (type === 'TRUE_FALSE' ? ['True', 'False'] : ['', '', '', '']).map((value) => ({ id: nanoid(), value })),
  correctAnswer: type === 'MULTIPLE_RESPONSE' ? [] : '',
  typeData: { answerMode: 'exact' },
});

export interface ExamQuestionPayload extends Record<string, unknown> {
  type: string;
  prompt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  kind: SectionKind;
  /** Existing question to edit; omitted to add a new one. */
  question?: Record<string, any> | null;
  uploadContext: string;
  onSave: (payload: ExamQuestionPayload) => Promise<void>;
  /** A Writing test about one task: the question is always that task. */
  fixedWritingTask?: IeltsWritingTask;
}

/** Question editor for exam sections: only the types the section uses, plus SAT/IELTS fields. */
export function ExamQuestionDialog({ open, onClose, kind, question, uploadContext, onSave, fixedWritingTask }: Props) {
  const { t } = useTranslation();
  const types = TYPES_FOR_KIND[kind] ?? ['MULTIPLE_CHOICE'];
  const [value, setValue] = useState<QuestionAuthoringValue>(() => fresh(types[0] as QuestionType));
  const [points, setPoints] = useState(1);
  const [domain, setDomain] = useState('');
  const [difficulty, setDifficulty] = useState<QuestionDifficulty | ''>('');
  const [chosenTask, setWritingTask] = useState<IeltsWritingTask>('TASK_2');
  const writingTask = fixedWritingTask ?? chosenTask;
  const [sprKeys, setSprKeys] = useState<string[]>([]);
  const [speaking, setSpeaking] = useState(() => speakingDraftFrom(undefined));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (!question) {
      setValue(fresh(types[0] as QuestionType));
      setPoints(1);
      setDomain('');
      setDifficulty('');
      setWritingTask('TASK_2');
      setSprKeys([]);
      setSpeaking(speakingDraftFrom(undefined));
      return;
    }
    const normalized = normalizeQuestionAnswers(question.options, question.correctAnswer);
    const data: QuestionTypeData = {};
    for (const key of TYPE_FIELDS) if (question[key] !== undefined) (data as Record<string, unknown>)[key] = question[key];
    setValue({
      type: question.type as QuestionType,
      prompt: question.prompt ?? '',
      explanation: question.explanation ?? '',
      options: normalized.options,
      correctAnswer: normalized.correctAnswer ?? '',
      matchPairs: question.matchPairs ?? [],
      typeData: data,
    });
    setPoints(question.points ?? 1);
    setDomain(question.domain ?? '');
    setDifficulty(question.difficulty ?? '');
    setWritingTask(question.writingTask ?? fixedWritingTask ?? 'TASK_2');
    setSprKeys(Array.isArray(question.acceptedAnswers) ? question.acceptedAnswers : []);
    setSpeaking(speakingDraftFrom(question.speakingParts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, question]);

  const isSpr = value.type === 'SPR';
  const isSpeaking = value.type === 'SPEAKING';
  const speakingIssue = isSpeaking ? speakingDraftIssue(speaking) : null;
  const payload: ExamQuestionPayload = isSpeaking
    ? {
        type: 'SPEAKING',
        prompt: 'IELTS Speaking',
        speakingParts: speakingPartsFrom(speaking),
        points,
        ...(difficulty ? { difficulty } : {}),
      }
    : {
    type: value.type,
    prompt: value.prompt,
    options: value.options,
    correctAnswer: isSpr ? sprKeys[0] ?? '' : value.correctAnswer,
    matchPairs: value.matchPairs,
    explanation: value.explanation,
    points,
    ...value.typeData,
    ...(isSpr ? { acceptedAnswers: sprKeys } : {}),
    ...(isSatKind(kind) && domain ? { domain } : {}),
    ...(difficulty ? { difficulty } : {}),
    ...(kind === 'IELTS_WRITING' ? { writingTask } : {}),
  };
  if (isSpr || value.type === 'OPEN_ENDED') delete payload.answerMode;
  // Speaking has its own editor and checks; the generic validator does not know the type.
  const validation = isSpeaking ? { isValid: !speakingIssue, issues: [] } : validateQuestion(payload as any);
  const { formRef, revealErrors, visibleIssues } = useQuestionValidationFeedback(validation.issues);

  const save = async () => {
    revealErrors();
    if (speakingIssue) {
      setError(t(speakingIssue));
      return;
    }
    if (saving || !validation.isValid) return;
    setSaving(true);
    setError('');
    try {
      await onSave(payload);
      onClose();
    } catch (cause: any) {
      setError(cause?.response?.data?.message || (cause instanceof Error ? cause.message : t('failed_to_save')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={t(question ? 'edit_question' : 'add_question')} size="xl">
      <QuestionValidationProvider issues={visibleIssues}>
        <div ref={formRef} className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
          <div className="flex flex-wrap gap-4">
            <label className="space-y-1.5 text-sm">
              <span className="font-medium">{t('question_type')}</span>
              <Select
                value={value.type}
                disabled={Boolean(question) || types.length === 1}
                onValueChange={(next) => setValue(fresh(next as QuestionType))}
              >
                <SelectTrigger className="min-h-10 w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(QUESTION_TYPE_META[type]?.labelKey || type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            {kind === 'IELTS_WRITING' && (
              <label className="space-y-1.5 text-sm">
                <span className="font-medium">{t('exam.writing.which_task')}</span>
                <Select value={writingTask} disabled={Boolean(fixedWritingTask)} onValueChange={(v) => setWritingTask(v as IeltsWritingTask)}>
                  <SelectTrigger className="min-h-10 w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TASK_1">{t('exam.writing.task1')}</SelectItem>
                    <SelectItem value="TASK_2">{t('exam.writing.task2')}</SelectItem>
                  </SelectContent>
                </Select>
              </label>
            )}

            {isSatKind(kind) && (
              <label className="space-y-1.5 text-sm">
                <span className="font-medium">{t('exam.sat.domain')}</span>
                <Select value={domain || '__none'} onValueChange={(v) => setDomain(v === '__none' ? '' : v)}>
                  <SelectTrigger className="min-h-10 w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">{t('exam.sat.no_domain')}</SelectItem>
                    {SAT_DOMAINS[kind].map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}

            <label className="space-y-1.5 text-sm">
              <span className="font-medium">{t('exam.difficulty')}</span>
              <Select value={difficulty || '__none'} onValueChange={(v) => setDifficulty(v === '__none' ? '' : (v as QuestionDifficulty))}>
                <SelectTrigger className="min-h-10 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t('exam.difficulty_any')}</SelectItem>
                  <SelectItem value="easy">{t('exam.diff.easy')}</SelectItem>
                  <SelectItem value="medium">{t('exam.diff.medium')}</SelectItem>
                  <SelectItem value="hard">{t('exam.diff.hard')}</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>

          {isSpeaking ? (
            <SpeakingEditor value={speaking} onChange={setSpeaking} />
          ) : (
            <QuestionAuthoringFields
              value={value}
              onChange={(patch) => setValue((prev) => ({ ...prev, ...patch }))}
              uploadContext={uploadContext}
            />
          )}

          {isSpr && <SprAnswersEditor value={sprKeys} onChange={setSprKeys} />}

          <label className="block max-w-[10rem] space-y-1.5 text-sm">
            <span className="font-medium">{t('points')}</span>
            <Input type="number" min={0.01} step={0.01} value={points} onChange={(e) => setPoints(Number(e.target.value))} />
          </label>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {t(saving ? 'saving' : 'save')}
          </Button>
        </div>
      </QuestionValidationProvider>
    </Modal>
  );
}

export default ExamQuestionDialog;
